import { useState, useEffect, useRef } from "react";
import { ArrowRight, Brain, FileText, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/shared/Logo";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ProcessingErrorAlert } from "@/components/ui/shared/ProcessingErrorAlert";

export const LandingPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const emailSectionRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add("dark");
    document.body.style.backgroundColor = "#000";
    document.body.classList.add("landing-page");
    const preloadImage = new Image();
    preloadImage.src = "https://bikusyozqktomenrosbg.supabase.co/storage/v1/object/public/app-assets/Hero-Image.webp";
    preloadImage.onload = () => setImageLoaded(true);
    return () => {
      document.documentElement.classList.remove("dark");
      document.body.style.backgroundColor = "";
      document.body.classList.remove("landing-page");
    };
  }, []);

  const handleSignIn = () => {
    navigate("/auth");
  };

  const handleJoinWaitlist = async () => {
    if (!email) {
      emailSectionRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      console.log("Submitting email to waitlist:", email);
      const {
        data,
        error: invokeError
      } = await supabase.functions.invoke('send-waitlist-email', {
        body: {
          email
        }
      });
      if (invokeError) {
        console.error("Error from edge function:", invokeError);
        throw new Error(invokeError.message || "Failed to send waitlist email");
      }
      if (data?.error) {
        console.error("Error response from edge function:", data.error);
        throw new Error(data.error || "Failed to send waitlist email");
      }
      console.log("Waitlist submission successful:", data);
      toast.success("Thank you for joining our waitlist!");
      setEmail("");
    } catch (err: any) {
      console.error("Error in waitlist submission:", err);
      const errorMessage = err.message || "Something went wrong. Please try again later.";
      setError(errorMessage);
      setIsErrorDialogOpen(true);
      toast.error("Failed to join waitlist");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToEmailSection = () => {
    emailSectionRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  const retrySubmission = () => {
    setIsErrorDialogOpen(false);
    handleJoinWaitlist();
  };

  const glowingTextStyle: React.CSSProperties = {
    color: '#10b981',
    position: 'relative',
    zIndex: 2
  };

  return <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <header className="px-4 sm:px-6 py-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <Logo variant="landing" showTagline={false} size="small" />
          </div>
          <Button 
            variant="ghost" 
            onClick={handleSignIn} 
            className="text-white hover:bg-white/10 hover:text-white/90"
          >
            Sign In
          </Button>
        </div>
      </header>

      <section className="py-32 px-4 sm:px-6 relative hero-section min-h-[80vh] flex items-center">
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img alt="Hero Background" src="/lovable-uploads/89d9c683-a5fb-46d1-97c9-63bf0a6b79ae.png" className="w-full h-full object-cover opacity-100 absolute top-0 left-0" />
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10 w-full">
          <div className="mb-8 relative z-10 hero-heading-container">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-none mb-0 hero-glow">
              Your Brand Voice.
            </h1>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-none mt-0 mb-8" style={glowingTextStyle}>
              Instantly Scalable.
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto">
              Product descriptions tailored to your unique brand identity.
              Consistent, compelling, and localized for every market.
            </p>
          </div>
          
          <div className="mt-12 relative z-10">
            <Button 
              className="rounded-full px-8 py-4 h-auto text-lg bg-white text-black hover:bg-gray-200" 
              onClick={scrollToEmailSection}
            >
              Join the Waitlist <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 border-t border-gray-800" ref={emailSectionRef}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Input 
              type="email" 
              placeholder="Enter your email" 
              className="max-w-xs sm:max-w-sm bg-gray-900 border-gray-700 focus:border-emerald-500" 
              value={email} 
              onChange={e => setEmail(e.target.value)} 
            />
            <Button 
              className="bg-white text-black hover:bg-gray-200" 
              onClick={handleJoinWaitlist} 
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Join Now"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 border-t border-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simplify your workflow</h2>
          <p className="text-gray-300 mb-16">
            Our intuitive platform streamlines your content creation process
          </p>
          
          <div className="grid md:grid-cols-3 gap-12">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center bg-gray-900/50 backdrop-blur-sm border border-emerald-500/30 mb-6 relative">
                <span className="text-emerald-400 font-medium text-xl">1</span>
              </div>
              <h3 className="text-xl font-bold mb-3">Effortless Batch Processing</h3>
              <p className="text-gray-400">
                Upload and process your entire product catalog in one go, saving hours of manual work.
              </p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center bg-gray-900/50 backdrop-blur-sm border border-emerald-500/30 mb-6 relative">
                <span className="text-emerald-400 font-medium text-xl">2</span>
              </div>
              <h3 className="text-xl font-bold mb-3">Intuitive Interface</h3>
              <p className="text-gray-400">
                Quickly review, edit, and approve content directly on our user-friendly platform.
              </p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center bg-gray-900/50 backdrop-blur-sm border border-emerald-500/30 mb-6 relative">
                <span className="text-emerald-400 font-medium text-xl">3</span>
              </div>
              <h3 className="text-xl font-bold mb-3">Seamless Export & Integration</h3>
              <p className="text-gray-400">
                Instantly export finished descriptions or easily integrate them into existing systems.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-24 px-4 sm:px-6 border-t border-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <blockquote className="text-2xl sm:text-3xl font-medium mb-6">
            "Consistent brand voice is no longer a luxury, but a necessity in the global marketplace."
          </blockquote>
          <cite className="text-gray-400">— Dan Lindgren, Co-founder, descripted.ai</cite>
        </div>
      </section>
      
      <section className="py-24 px-4 sm:px-6 border-t border-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Features that make a difference</h2>
          <p className="text-gray-300 mb-16">
            Our platform is designed to transform how you create product content
          </p>
          
          <div className="grid md:grid-cols-3 gap-12">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gray-900 border border-gray-800 mb-6">
                <Brain className="h-6 w-6 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold mb-3">Brand-Aware Models</h3>
              <p className="text-gray-400">
                AI trained to embody your brand's unique tone, ensuring consistency across all your product descriptions.
              </p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gray-900 border border-gray-800 mb-6">
                <FileText className="h-6 w-6 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold mb-3">High-Quality Content</h3>
              <p className="text-gray-400">
                Refined descriptions that meet exact brand standards, crafted to engage your audience and drive conversions.
              </p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gray-900 border border-gray-800 mb-6">
                <Globe className="h-6 w-6 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold mb-3">Localized Translations</h3>
              <p className="text-gray-400">
                Translations that maintain the authenticity of your original brand content across multiple markets and languages.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      <footer className="py-12 px-4 sm:px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <Logo variant="landing" showTagline={false} size="small" />
            </div>
            
            <div className="flex gap-6 text-gray-400">
              <a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="mailto:contact@descripted.ai" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
          
          <div className="mt-10 pt-6 border-t border-gray-800 text-center text-gray-500 text-sm">
            © 2025 descripted.ai. All rights reserved.
          </div>
        </div>
      </footer>

      <ProcessingErrorAlert isOpen={isErrorDialogOpen} onClose={() => setIsErrorDialogOpen(false)} errorDetails={error || undefined} />
    </div>;
};
